import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { handleGetItemsWithParam } from "../../shared/controllers/blogController";
import { unwrapApiArray } from "../../shared/utils/apiResponse";
import landingAsset from "../utils/assetPaths";

interface Heading {
    category: string;
    title?: string;
    excerpt?: string;
}

interface Reason {
    title: string;
    excerpt: string;
    image: string;
}

interface ChooseSacaProps {
    headings: Heading[];
}

const ChooseSaca: React.FC<ChooseSacaProps> = ({ headings }) => {
    const [reasons, setReasons] = useState<Reason[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [loading, setLoading] = useState(true);
    const [pageTitle, setPageTitle] = useState("Why Choose SACA?");
    const [pageExcerpt, setPageExcerpt] = useState(
        "More than a booking platform, SACA connects sports lovers to play, compete, and stay engaged effortlessly."
    );

    const fallbackReasons: Reason[] = [
        {
            title: "Affordable and Convenient Access",
            excerpt:
                "Enjoy subsidized rates for pitch bookings and a simple, easy-to-use platform. Whether you're booking a match or checking league fixtures, SACA offers it all with a budget-friendly subscription.",
            image: landingAsset("choice1.svg"),
        },
        {
            title: "All-in-One Sports Platform",
            excerpt:
                "SACA provides a seamless experience by combining pitch bookings, match participation, and league tracking in one place. Stay engaged with your favorite sports activities without the hassle of managing multiple platforms.",
            image: landingAsset("choice2.svg"),
        },
    ];

    const fetchReasons = async () => {
        const params = { type: "Reason" };
        try {
            const data = await handleGetItemsWithParam<Reason[]>(params);
            setReasons(unwrapApiArray<Reason>(data));
        } catch (error) {
            console.error("Error fetching reasons:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReasons();
    }, []);

    useEffect(() => {
        const whyHeading = headings.find((heading) => heading.category === "Why");
        if (whyHeading) {
            setPageTitle(whyHeading.title || "Why Choose SACA?");
            setPageExcerpt(
                whyHeading.excerpt ||
                "More than a booking platform, SACA connects sports lovers to play, compete, and stay engaged effortlessly."
            );
        }
    }, [headings]);

    const reasonsToRender = reasons.length > 0 ? reasons : fallbackReasons;

    return (
        <div className="flex flex-col items-center py-16 mt-16 w-full px-4 md:px-14">
            <h3 className="text-center">{pageTitle}</h3>
            <h2 className="text-center mt-3 max-w-[900px]">{pageExcerpt}</h2>

            {/* reason 1 */}
            <div className="mt-16 w-full flex flex-col md:flex-row items-start justify-between">
                <div
                    style={{
                        backgroundImage: `url(${reasonsToRender[0]?.image || fallbackReasons[0]?.image || ""
                            })`,
                        backgroundPosition: "center",
                        backgroundSize: "cover",
                    }}
                    className="w-full md:w-[48%] h-[500px] rounded-[15px] bg-[#B3834D]"
                ></div>

                <div className="w-full md:w-[48%] mt-4 md:mt-0">
                    <h2 className="text-[#000000] font-Manrope font-bold text-2xl">
                        {reasonsToRender[0]?.title || fallbackReasons[0]?.title || "Affordable Access"}
                    </h2>
                    <p className="font-Manrope font-normal text-black text-base mt-3">
                        {reasonsToRender[0]?.excerpt || fallbackReasons[0]?.excerpt || ""}
                    </p>
                    <Link to="/sign-up">
                        <button className="mt-12 block bg-[#317333] px-6 py-4 font-Manrope font-semibold text-lg text-white rounded-[6px]">
                            Get Started
                        </button>
                    </Link>
                </div>
            </div>

            {/* reason 2 */}
            <div className="mt-16 w-full flex flex-col-reverse md:flex-row items-start justify-between">
                <div className="w-full mt-4 md:mt-0 md:w-[48%]">
                    <h2 className="text-[#000000] font-Manrope font-bold text-2xl">
                        {reasonsToRender[1]?.title || fallbackReasons[1]?.title || "All-in-One Platform"}
                    </h2>
                    <p className="font-Manrope font-normal text-black text-base mt-3">
                        {reasonsToRender[1]?.excerpt || fallbackReasons[1]?.excerpt || ""}
                    </p>
                    <Link to="/sign-up">
                        <button className="mt-12 block bg-[#317333] px-6 py-4 font-Manrope font-semibold text-lg text-white rounded-[6px]">
                            Get Started
                        </button>
                    </Link>
                </div>

                <div
                    style={{
                        backgroundImage: `url(${reasonsToRender[1]?.image || fallbackReasons[1]?.image || ""
                            })`,
                        backgroundPosition: "center",
                        backgroundSize: "cover",
                    }}
                    className="w-full md:w-[48%] h-[500px] rounded-[15px] bg-[#B3834D]"
                ></div>
            </div>
        </div>
    );
};

export default ChooseSaca;
